import { TextInputField } from 'evergreen-ui';
import React from 'react';
import { hasValue, isOutsideMinMax } from 'helper/validation';
import { dataValueRegexPattern, HEIGHT, onEnterBlur } from './utils';

/**
 * @param {object} props
 * @prop {number | string | undefined} value
 * @prop {({value, name, valueId?}) => void} updateValue функция для обновление значения value
 * @prop {object} confines
 * @prop {number | string} confines.minWarningValue предупреждение
 * @prop {number | string} confines.maxWarningValue предупреждение
 * @prop {number | string} confines.minValue строго
 * @prop {number | string} confines.maxValue строго
 * @prop {string} [formatValue] формат вводимого значения:
 * * %d -ввод любого числа
 * * %s или undefined - ввод любых символы
 *
 * @prop ...textInputFieldProps
 */
export const MeasureTextInputField = React.memo(({
  valueType,
  paramId,
  codeId,
  formatValue,
  confines: { minWarningValue, maxWarningValue, minValue, maxValue } = {},
  updateValue,
  ...inputProps
}) => {
  // чтобы проще было отличать атрибуты от "собственных"
  const {
    label = '',
    value,
    onKeyPress,
    onChange,
  } = inputProps;

  const { pattern, inputMode } = React.useMemo(() => ({
    pattern: formatValue === '%d' ? dataValueRegexPattern : undefined,
    inputMode: formatValue === '%d' ? 'decimal' : 'text',
  }), [formatValue]);

  const { classNameConfines, messageConfines, isInvalidConfines } = React.useMemo(() => {
    const isDangerValue = isOutsideMinMax({ value, min: minValue, max: maxValue });
    const isWarningValue = !isDangerValue && isOutsideMinMax({ value, min: minWarningValue, max: maxWarningValue });
    let message = null; // null - т.к. при '' инпут будет показывать иконку

    if (value) {
      if (isDangerValue) message = `Границы: ${getPlaceholderMinMax(minValue, maxValue)}`;
      else if (isWarningValue) message = `Предупреждение: ${getPlaceholderMinMax(minWarningValue, maxWarningValue)}`;
    }

    return {
      classNameConfines: isWarningValue ? 'warning-invalid' : '',
      messageConfines: message,
      isInvalidConfines: isDangerValue || isWarningValue,
    };
  }, [maxValue, maxWarningValue, minValue, minWarningValue, value]);

  const placeholder = React.useMemo(() => (
    inputProps.placeholder || getPlaceholderMinMax(minValue, maxValue)
  ), [inputProps.placeholder, maxValue, minValue]);

  const thisUpdateValue = React.useCallback((e) => {
    updateValue && updateValue({ value: e.target.value, name: e.target.name });
    onChange && onChange(e);
  }, [onChange, updateValue]);

  const thisOnKeyPress = React.useCallback((e) => {
    onEnterBlur(e);
    onKeyPress && onKeyPress(e);
  }, [onKeyPress]);

  return (
    <TextInputField
      label={label}
      inputMode={inputMode}
      pattern={pattern}
      value={value || ''}
      onChange={thisUpdateValue}
      autoComplete="off"
      width="100%"
      inputHeight={HEIGHT}
      margin={0}
      {...inputProps}
      placeholder={placeholder}
      className={`program-measure-input ${classNameConfines}`}
      isInvalid={isInvalidConfines || inputProps.isInvalid}
      validationMessage={messageConfines || inputProps.validationMessage}
      onKeyPress={thisOnKeyPress}
    />
  );
});
MeasureTextInputField.displayName = 'MeasureTextInputField';

function getPlaceholderMinMax(minValue, maxValue) {
  let placeholder = 'Введите значение';
  if (hasValue(minValue) && hasValue(maxValue)) {
    placeholder = `от ${minValue} до ${maxValue}`;
  } else if (hasValue(minValue)) placeholder = `от ${minValue}`;
  else if (hasValue(maxValue)) placeholder = `до ${maxValue}`;
  return placeholder;
}
